import type {
  PullRequestActivity,
  PullRequestTimelineComment,
  PullRequestTimelineCommit,
} from "@afar-research/contracts";

/**
 * One pull request's history as a single ordered rail, following T3Code.
 *
 * The Environment reports activity, commits and comments as three lists, each with its own
 * clock. A reader is not reading three lists: they are reading what happened, in the order it
 * happened. Consecutive pushes collapse into one entry, because ten commits landing together is
 * one event to the reader and ten rows to the host.
 */

export type PullRequestTimelineEntry =
  | { readonly kind: "activity"; readonly at: string; readonly activity: PullRequestActivity }
  | {
      readonly kind: "commits";
      readonly at: string;
      readonly commits: ReadonlyArray<PullRequestTimelineCommit>;
    }
  | { readonly kind: "comment"; readonly at: string; readonly comment: PullRequestTimelineComment };

/** Ties keep the host's own order within a kind, so a rebuild never reshuffles equal timestamps. */
function byTime(left: { readonly at: string }, right: { readonly at: string }): number {
  return left.at.localeCompare(right.at);
}

export function pullRequestTimelineEntries(input: {
  readonly activity: ReadonlyArray<PullRequestActivity>;
  readonly commits: ReadonlyArray<PullRequestTimelineCommit>;
  readonly comments: ReadonlyArray<PullRequestTimelineComment>;
}): ReadonlyArray<PullRequestTimelineEntry> {
  const merged: PullRequestTimelineEntry[] = [
    ...input.activity.map(
      (activity) => ({ kind: "activity", at: activity.occurredAt, activity }) as const,
    ),
    ...input.commits.map(
      (commit) => ({ kind: "commits", at: commit.committedAt, commits: [commit] }) as const,
    ),
    ...input.comments.map(
      (comment) => ({ kind: "comment", at: comment.createdAt, comment }) as const,
    ),
  ];
  merged.sort(byTime);

  // A run of commits with nothing between them is one push to the reader.
  const entries: PullRequestTimelineEntry[] = [];
  for (const entry of merged) {
    const previous = entries.at(-1);
    if (entry.kind === "commits" && previous?.kind === "commits") {
      entries[entries.length - 1] = {
        kind: "commits",
        // The run is dated by its last commit: that is when the branch arrived at this state.
        at: entry.at,
        commits: [...previous.commits, ...entry.commits],
      };
      continue;
    }
    entries.push(entry);
  }
  return entries;
}

/**
 * A row of the rail. Following T3Code, a run of consecutive remarks is one conversation rather
 * than one row each: a bot that posts eight times in a row is one thing that happened, and
 * spelling it out costs the reader every other event on the page.
 */
export type PullRequestTimelineRow =
  // Grouping takes every remark into a conversation, so an `entry` row is never a comment.
  | {
      readonly kind: "entry";
      readonly entry: Exclude<PullRequestTimelineEntry, { kind: "comment" }>;
    }
  | {
      readonly kind: "conversation";
      readonly comments: ReadonlyArray<PullRequestTimelineComment>;
      readonly at: string;
    };

export function groupPullRequestTimelineConversations(
  entries: ReadonlyArray<PullRequestTimelineEntry>,
): ReadonlyArray<PullRequestTimelineRow> {
  const rows: PullRequestTimelineRow[] = [];
  for (const entry of entries) {
    if (entry.kind !== "comment") {
      rows.push({ kind: "entry", entry });
      continue;
    }
    const last = rows.at(-1);
    if (last?.kind === "conversation") {
      rows[rows.length - 1] = {
        kind: "conversation",
        comments: [...last.comments, entry.comment],
        // Dated by the remark that opened it, which is what the collapsed row reports.
        at: last.at,
      };
      continue;
    }
    rows.push({ kind: "conversation", comments: [entry.comment], at: entry.at });
  }
  return rows;
}

/** Everyone who spoke in one conversation, first appearance first, each named once. */
export function conversationActors(
  comments: ReadonlyArray<PullRequestTimelineComment>,
): ReadonlyArray<NonNullable<PullRequestTimelineComment["author"]>> {
  const seen = new Map<string, NonNullable<PullRequestTimelineComment["author"]>>();
  for (const comment of comments) {
    if (comment.author === null) continue;
    if (!seen.has(comment.author.login)) seen.set(comment.author.login, comment.author);
  }
  return [...seen.values()];
}
